import { useNavigate } from '@tanstack/react-router';
import { Mail, MessageSquare, Plus, RefreshCcw, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PLATFORMS } from '../../data/mockData';
import { useStore } from '../../store/useStore';

const STAGE_LABELS = ['新询盘', '已报价', '谈判中', '待付款', '已成交'];
const PLATFORM_OPTIONS = ['email', 'telegram', 'whatsapp', 'wecom', 'teams'];
const CONFIGURE_SOCIAL_TITLE = '请先配置客户邮箱、Telegram、WhatsApp、企业微信或 Teams 账号';

function hasUsableEmail(contact) {
  return Boolean(contact?.email && contact.email.includes('@'));
}

function getAvailableStartPlatforms(contact) {
  const platforms = new Set(contact?.platforms || []);
  const available = [];
  if (hasUsableEmail(contact) || platforms.has('email')) {
    available.push('email');
  }
  if (platforms.has('telegram')) {
    available.push('telegram');
  }
  if (platforms.has('whatsapp')) {
    available.push('whatsapp');
  }
  if (platforms.has('wecom')) {
    available.push('wecom');
  }
  if (platforms.has('teams')) {
    available.push('teams');
  }
  return available;
}

export default function CustomersView() {
  const navigate = useNavigate();
  const {
    contactSearchQuery,
    contactsLoading,
    error,
    getContactList,
    setContactSearchQuery,
    loadContacts,
    openContact,
    startConversation,
    createContact,
    deleteContact,
    conversations,
  } = useStore();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', company: '', countryName: '', email: '' });
  const [startPlatforms, setStartPlatforms] = useState({});

  const contacts = getContactList();

  const handleCreate = async () => {
    if (!draft.name.trim()) return;
    const contact = await createContact({
      name: draft.name.trim(),
      company: draft.company.trim(),
      countryName: draft.countryName.trim(),
      email: draft.email.trim(),
    });
    setDraft({ name: '', company: '', countryName: '', email: '' });
    setAdding(false);
    setContactSearchQuery('');
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`删除客户「${contact.name}」？仅无会话客户可删除。`)) return;
    await deleteContact(contact.id);
  };

  const getStartPlatform = (contact) => {
    const availablePlatforms = getAvailableStartPlatforms(contact);
    const selected = startPlatforms[contact.id];
    return availablePlatforms.includes(selected) ? selected : availablePlatforms[0] || '';
  };

  const handleOpenContact = async (contactId) => {
    await openContact(contactId);
    navigate({ to: '/messages' });
  };

  const handleStartConversation = async (contactId, platform) => {
    await startConversation(contactId, platform);
    navigate({ to: '/messages' });
  };

  return (
    <main className="customers-view">
      <div className="customers-header">
        <div>
          <div className="customers-title">客户</div>
          <div className="customers-subtitle">{contacts.length} 个客户</div>
        </div>
        <div className="customers-actions">
          <button className="icon-btn" onClick={() => setAdding(true)} title="新增客户">
            <Plus size={16} />
          </button>
          <button className="icon-btn" onClick={loadContacts} title="刷新客户" disabled={contactsLoading}>
            <RefreshCcw size={16} />
          </button>
        </div>
      </div>

      <div className="customers-toolbar">
        <div className="search-bar customers-search">
          <Search size={14} color="var(--c-text-3)" />
          <input
            placeholder="搜索客户、公司、标签..."
            value={contactSearchQuery}
            onChange={event => setContactSearchQuery(event.target.value)}
          />
        </div>
      </div>

      {error && <div className="tasks-error">{error}</div>}

      {adding && (
        <div className="customer-create">
          <input
            placeholder="客户姓名"
            value={draft.name}
            autoFocus
            onChange={event => setDraft({ ...draft, name: event.target.value })}
          />
          <input
            placeholder="公司"
            value={draft.company}
            onChange={event => setDraft({ ...draft, company: event.target.value })}
          />
          <input
            placeholder="国家 / 地区"
            value={draft.countryName}
            onChange={event => setDraft({ ...draft, countryName: event.target.value })}
          />
          <input
            placeholder="邮箱"
            type="email"
            value={draft.email}
            onChange={event => setDraft({ ...draft, email: event.target.value })}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleCreate();
              }
            }}
          />
          <button className="settings-save" onClick={handleCreate}>
            <Plus size={15} />
            <span>新增</span>
          </button>
          <button className="secondary-mini" onClick={() => setAdding(false)}>取消</button>
        </div>
      )}

      <div className="customers-table">
        <div className="customers-table-head">
          <span>客户</span>
          <span>阶段</span>
          <span>标签</span>
          <span>负责人</span>
          <span />
        </div>

        {contactsLoading && <div className="customers-empty">正在加载客户...</div>}
        {!contactsLoading && contacts.length === 0 && <div className="customers-empty">没有找到匹配客户</div>}

        {contacts.map(contact => {
          const hasConversation = conversations.some(conv => conv.contactId === contact.id);
          const hasEmailConversation = conversations.some(conv => conv.contactId === contact.id && conv.platform === 'email');
          const availableStartPlatforms = getAvailableStartPlatforms(contact);
          const selectedStartPlatform = getStartPlatform(contact);
          const canStartConversation = availableStartPlatforms.length > 0;
          const startConversationTitle = canStartConversation ? '开始会话' : CONFIGURE_SOCIAL_TITLE;
          return (
          <div key={contact.id} className="customer-row">
            <div className="customer-main">
              <div className="contact-avatar" style={{ background: contact.avatarBg, color: contact.avatarColor }}>
                {contact.initials}
              </div>
              <div className="customer-info">
                <div className="customer-name">{contact.name}</div>
                <div className="customer-company">{contact.company || '未填写公司'} · {contact.country || '未知地区'}</div>
                <div className="customer-email">{contact.email || '未添加邮箱'}</div>
              </div>
            </div>
            <div className="customer-stage">{STAGE_LABELS[contact.stage || 0] || '未设置'}</div>
            <div className="customer-tags">
              {(contact.tags || []).slice(0, 3).map(tag => (
                <span key={tag} className="tag tag-gray">{tag}</span>
              ))}
            </div>
            <div className="customer-assignee">
              <span className="mini-avatar">{contact.assignee?.initials || '?'}</span>
              <span>{contact.assignee?.name || '未分配'}</span>
            </div>
            <div className="customer-actions">
              <button
                className="task-open"
                onClick={() => handleStartConversation(contact.id, 'email')}
                title={contact.email ? (hasEmailConversation ? '打开邮件会话' : '新建邮件会话') : '请先在客户资料中添加邮箱'}
                disabled={!contact.email}
              >
                <Mail size={15} />
                <span>邮件</span>
              </button>
              {hasConversation ? (
                <button className="task-open" onClick={() => handleOpenContact(contact.id)} title="打开会话">
                  <MessageSquare size={15} />
                  <span>打开</span>
                </button>
              ) : (
                <>
                  <select
                    className="customer-platform-select"
                    value={selectedStartPlatform}
                    onChange={event => setStartPlatforms({ ...startPlatforms, [contact.id]: event.target.value })}
                    title={canStartConversation ? '选择渠道' : CONFIGURE_SOCIAL_TITLE}
                    disabled={!canStartConversation}
                  >
                    {canStartConversation ? (
                      availableStartPlatforms.map(platform => (
                        <option key={platform} value={platform}>{PLATFORMS[platform]?.short || platform}</option>
                      ))
                    ) : (
                      <option value="">未配置</option>
                    )}
                  </select>
                  <button
                    className="task-open"
                    onClick={() => {
                      if (!canStartConversation) return;
                      handleStartConversation(contact.id, selectedStartPlatform);
                    }}
                    title={startConversationTitle}
                    disabled={!canStartConversation}
                  >
                    <MessageSquare size={15} />
                    <span>开始</span>
                  </button>
                </>
              )}
              <button className="customer-delete" onClick={() => handleDelete(contact)} title="删除客户">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        );})}
      </div>
    </main>
  );
}
